import React, { useState, useEffect, useCallback } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import DownloadIcon from "@styles/all-images/icons/download.svg";
import ListIcon from "@styles/all-images/icons/list-icon.svg";
import UnarchiveIcon from "@styles/all-images/icons/unarchived.svg";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import { useNavigate } from "react-router-dom";
import StaffAdd from "./AddProfiles";
import { useDispatch } from "react-redux";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import { AppThunkDispatch } from "@/store";
import CustomTable, {
  TableColumn,
} from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import { Tooltip } from "@mui/material";
import { usePermissionState } from "@/providers/PermissionProvider";
import { ProfileManagementPermissions } from "@/constants/permission-modules/base-permissions";

interface Role {
  id: number;
  name: string;
}

interface Profile {
  id: number;
  name: string;
  created_date: string;
  assigned_count: number;
  roles: Role[];
}

const StaffManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();

  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage] = useState(5);
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [columns, setColumns] = useState<TableColumn<Profile>[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [profileToArchive, setProfileToArchive] = useState<any>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const { isPermitted } = usePermissionState();

  const handleOpenStaffModal = () => {
    setIsStaffModalOpen(true);
  };

  const handleCloseStaffModal = () => {
    setIsStaffModalOpen(false);
  };

  const handleStaffSubmit = () => {
    setIsStaffModalOpen(false);
  };

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { authenticatedCall } = actionsHandler("permissionProfile");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: new URLSearchParams({
              limit: params.limit.toString(),
              page: params.page.toString(),
              search: params.search || "",
              is_archive: isArchiveView ? "true" : "false",
              ...(params.sortColumn && {
                ordering:
                  (params.sortDirection === "asc" ? "" : "-") +
                  params.sortColumn,
              }),
            }).toString(),
          }),
        );
        return response;
      } catch (error) {
        console.error("Error fetching profiles:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, limit: 10 },
          },
        };
      }
    },
    [dispatch, isArchiveView],
  );

  const fetchProfileDetails = async (id: number) => {
    try {
      const { authenticatedCall } = actionsHandler("permissionProfileById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [id.toString()],
        }),
      );

      if (response.ok) {
        return response.data;
      }
      toast.error("Failed to fetch profile details");
      return null;
    } catch (error) {
      console.error("Error fetching profile details:", error);
      toast.error("Unable to fetch profile details");
      return null;
    }
  };

  const handleArchived = async (row: any, isCurrentlyArchived: boolean) => {
    setProfileToArchive({ row, isCurrentlyArchived });
    setShowConfirmModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!profileToArchive) return;

    try {
      setIsArchiving(true);
      const { authenticatedCall: archiveProfile } = actionsHandler(
        "permissionProfileById",
      );

      const payload = {
        is_archive: !profileToArchive.isCurrentlyArchived,
      };

      const response = await dispatch(
        archiveProfile({
          payload,
          method: "patch",
          customParams: [profileToArchive.row.id + "/"],
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            `Profile ${payload.is_archive ? "archived" : "unarchived"} successfully`,
        );
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Archive error:", error);
    } finally {
      setIsArchiving(false);
      setShowConfirmModal(false);
      setProfileToArchive(null);
    }
  };

  const handleSettingColumns = useCallback(() => {
    setColumns([
      {
        header: "Profile ID",
        accessor: (row: any) => <span>{row.id}</span>,
        sortable: true,
        sortKey: "id",
      },
      {
        header: "Profile Name",
        accessor: (row: any) => (
          <div className="tw-max-w-[200px]">
            <Tooltip title={row.name} placement="top">
              <span className="tw-block tw-truncate">{row.name}</span>
            </Tooltip>
          </div>
        ),
        sortable: true,
        sortKey: "name",
      },
      {
        header: isArchiveView ? "Archived Date" : "Created Date",
        accessor: (row: any) => (
          <span>
            {formatDate(row.is_archive ? row.updated_at : row.created_at)}
          </span>
        ),
        sortable: true,
        sortKey: isArchiveView ? "updated_at" : "created_at",
      },
      ...(!isArchiveView
        ? [
            {
              header: "Profile Assigned Count",
              accessor: (row: any) => (
                <div className="tw-flex tw-items-center">
                  <span>{row.profile_assign_count}</span>
                  <button
                    onClick={() => handleViewAssignments(row.id)}
                    title="View"
                  >
                    <img
                      src={EyeIcon}
                      alt="View Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                </div>
              ),
              sortKey: "profile_assign_count",
            },
          ]
        : []),
      {
        header: "Roles Assigned",
        accessor: (row: Profile) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            {row.roles.slice(0, 4).map((role: Role) => (
              <Tooltip key={role.id} title={role.name} placement="top">
                <span className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323] tw-cursor-default tw-max-w-[80px] tw-truncate tw-inline-block">
                  {role.name}
                </span>
              </Tooltip>
            ))}
            {row.roles.length > 4 && (
              <span className="tw-px-2 tw-bg-[#146c94] custom-chip-bg tw-py-1 tw-border tw-border-[#fff] tw-rounded-full tw-text-sm tw-text-[#fff] tw-cursor-default">
                +{row.roles.length - 4}
              </span>
            )}
          </div>
        ),
        sortable: false,
      },
      {
        header: `${!isArchiveView ? "Actions" : "Action"}`,
        accessor: (row: any) => (
          <div className="tw-flex tw-items-center">
            {!isArchiveView ? (
              <>
                {isPermitted(
                  ProfileManagementPermissions.UPDATE_PROFILE_MANAGEMENT,
                ) && (
                  <button onClick={() => handleEditClick(row.id)}>
                    <img
                      src={EditIcon}
                      alt="Edit Icon"
                      title="Edit"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                )}
                {isPermitted(
                  ProfileManagementPermissions.ARCHIVE_PROFILE_MANAGEMENT,
                ) && (
                  <button onClick={() => handleArchived(row, false)}>
                    <img
                      src={DownloadIcon}
                      title="Archive"
                      alt="Archive Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                )}
              </>
            ) : (
              isPermitted(
                ProfileManagementPermissions.ARCHIVE_PROFILE_MANAGEMENT,
              ) && (
                <button onClick={() => handleArchived(row, true)}>
                  <img
                    src={UnarchiveIcon}
                    alt="Unarchive Icon"
                    title="Unarchive"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              )
            )}
          </div>
        ),
        sortable: false,
      },
    ]);
  }, [isArchiveView]);

  const handleEditClick = async (id: number) => {
    try {
      const { authenticatedCall } = actionsHandler("permissionProfileById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [id.toString()],
        }),
      );

      if (response.ok) {
        setSelectedProfile(response.data);
        setIsStaffModalOpen(true);
      } else {
        toast.error("Failed to fetch profile details");
      }
    } catch (error) {
      console.error("Error fetching profile details:", error);
      toast.error("Unable to fetch profile details");
    }
  };

  const handleViewAssignments = (id: number) => {
    navigate(`/profile-assigned-users/${id}`);
  };

  const toggleArchiveView = () => {
    setIsArchiveView(!isArchiveView);
  };

  useEffect(() => {
    handleSettingColumns();
    setForceReset(true);
  }, [isArchiveView]);

  return (
    <div>
      {/* Sub Header */}
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a href="/">
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Profile Management
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-mt-[147px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          forceReset={forceReset}
          setForceReset={setForceReset}
          addButton={
            isPermitted(ProfileManagementPermissions.CREATE_PROFILE_MANAGEMENT)
              ? {
                  text: "Add Profile",
                  icon: PlusIcon,
                  onClick: () => {
                    setIsStaffModalOpen(true);
                  },
                }
              : undefined
          }
          archiveButton={{
            text: isArchiveView ? "Back to List" : "Archived",
            icon: isArchiveView ? ListIcon : DownloadIcon,
            onClick: toggleArchiveView,
            isArchiveView,
          }}
          noDataMessage={`No profile found`}
          noSearchDataMessage={`No matching profile found`}
        />

        <div className="tw-flex tw-justify-between tw-items-center tw-mt-4">
          <div
            className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-full tw-bg-black/25 tw-shadow-lg tw-transition-transform tw-duration-300 tw-ease-in-out ${
              isStaffModalOpen
                ? "tw-transform tw-translate-x-0"
                : "tw-transform tw-translate-x-full"
            } tw-z-50 tw-pointer-events-none`}
          >
            {isStaffModalOpen && (
              <div className="tw-pointer-events-auto">
                <StaffAdd
                  onClose={() => {
                    setIsStaffModalOpen(false);
                    setSelectedProfile(null);
                  }}
                  onSubmit={() => {
                    setForceRefresh(true);
                  }}
                  isStaffModalOpen={isStaffModalOpen}
                  editData={selectedProfile}
                />
              </div>
            )}
          </div>
        </div>

        {showConfirmModal && (
          <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
            <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
              <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
                <div></div>
                <h2 className="tw-text-base tw-font-semibold">
                  Confirm{" "}
                  {profileToArchive?.isCurrentlyArchived
                    ? "Unarchive"
                    : "Archive"}
                </h2>
                <button onClick={() => setShowConfirmModal(false)}>
                  <svg
                    className="tw-w-5 tw-h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
                <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                  Are you sure you want to{" "}
                  {profileToArchive?.isCurrentlyArchived
                    ? "unarchive "
                    : "archive "}{" "}
                  <span className="tw-inline-flex tw-items-center tw-justify-center">
                    <Tooltip
                      title={profileToArchive?.row?.name}
                      placement="top"
                    >
                      <span className="tw-max-w-[200px] tw-truncate tw-line-clamp-2 tw-inline-block">
                        {profileToArchive?.row?.name}
                      </span>
                    </Tooltip>
                    ?
                  </span>
                </h6>
              </div>

              <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isArchiving}
                  className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmArchive}
                  disabled={isArchiving}
                  className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                >
                  {isArchiving ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffManagement;
